import React, { useEffect, useMemo, useState } from 'react';
import './GigSubmission.css';
import {
  buildGigSubmissionRequestBody,
  createGigSubmissionForm,
  normalizeGigSubmissionResponse,
  validateGigSubmissionForm,
} from './gigSubmissionHelpers';
import { PREFERRED_CONTACT_METHOD_OPTIONS } from '../../utils/gigProfileFields';

const PROMOTION_SHARE_PLATFORM_CONFIG = [
  { field: 'instagramUrl', label: 'Instagram' },
  { field: 'facebookUrl', label: 'Facebook' },
  { field: 'tiktokUrl', label: 'TikTok' },
  { field: 'youtubeUrl', label: 'YouTube' },
  { field: 'twitterUrl', label: 'X' },
  { field: 'pinterestUrl', label: 'Pinterest' },
  { field: 'soundcloudUrl', label: 'SoundCloud' },
  { field: 'spotifyUrl', label: 'Spotify' },
  { field: 'mixcloudUrl', label: 'Mixcloud' },
  { field: 'appleMusicUrl', label: 'Apple Music' },
];

async function readJson(response) {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch (error) {
    throw new Error(text || 'Request failed');
  }
}

async function fetchSubmission(token) {
  const response = await fetch(`/api/gigs/submissions/${encodeURIComponent(token)}`);
  const data = await readJson(response);
  if (!response.ok) {
    throw new Error(data.message || 'Failed to load submission');
  }
  return normalizeGigSubmissionResponse(data);
}

async function presignArtwork(token, filename) {
  const response = await fetch(`/api/gigs/submissions/${encodeURIComponent(token)}/artwork/presign`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ filename }),
  });
  const data = await readJson(response);
  if (!response.ok) {
    throw new Error(data.message || 'Failed to start artwork upload');
  }
  return data;
}

async function finalizeArtwork(token, payload) {
  const response = await fetch(`/api/gigs/submissions/${encodeURIComponent(token)}/artwork/finalize`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  const data = await readJson(response);
  if (!response.ok) {
    throw new Error(data.message || 'Failed to finalize artwork upload');
  }
  return data;
}

async function submitForm(token, payload) {
  const response = await fetch(`/api/gigs/submissions/${encodeURIComponent(token)}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  const data = await readJson(response);
  if (!response.ok) {
    throw new Error(data.message || 'Failed to submit gig information');
  }
  return normalizeGigSubmissionResponse(data);
}

function formatDateRange(gig) {
  if (!gig?.startsAtUtc || !gig?.endsAtUtc) return 'Schedule to be confirmed';
  const start = new Date(gig.startsAtUtc);
  const end = new Date(gig.endsAtUtc);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 'Schedule to be confirmed';

  const dateLabel = start.toLocaleDateString([], {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  const startTimeLabel = start.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  });
  const endTimeLabel = end.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  });

  return `${dateLabel} from ${startTimeLabel} to ${endTimeLabel}`;
}

function buildNaturalLanguageList(items = []) {
  const values = (Array.isArray(items) ? items : [])
    .map((entry) => String(entry || '').trim())
    .filter(Boolean);

  if (!values.length) return '';
  if (values.length === 1) return values[0];
  if (values.length === 2) return `${values[0]} and ${values[1]}`;
  return `${values.slice(0, -1).join(', ')}, and ${values.at(-1)}`;
}

function getPromotionSharePlatforms(profile = {}) {
  return PROMOTION_SHARE_PLATFORM_CONFIG
    .filter((platform) => String(profile?.[platform.field] || '').trim())
    .map((platform) => platform.label)
    .slice(0, 3);
}

export default function GigSubmission({ match }) {
  const token = match?.params?.token || '';
  const [submission, setSubmission] = useState(null);
  const [form, setForm] = useState(() => createGigSubmissionForm({}));
  const [loading, setLoading] = useState(true);
  const [uploadingArtwork, setUploadingArtwork] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [submittedJustNow, setSubmittedJustNow] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        setError('');
        setSuccessMessage('');
        setSubmittedJustNow(false);
        const nextSubmission = await fetchSubmission(token);
        if (cancelled) return;
        setSubmission(nextSubmission);
        setForm(createGigSubmissionForm(nextSubmission));
      } catch (err) {
        if (cancelled) return;
        setError(err.message || 'Failed to load submission');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const scheduleLabel = useMemo(() => formatDateRange(submission?.gig), [submission]);
  const isCancelled = submission?.gig?.status === 'cancelled';
  const venueName = String(submission?.gig?.siteName || 'this venue').trim() || 'this venue';
  const gigTitle = String(submission?.gig?.title || 'this performance').trim() || 'this performance';
  const promotionSharePlatforms = useMemo(
    () => getPromotionSharePlatforms(submission?.performer || {}),
    [submission?.performer]
  );
  const promotionSharePlatformLabel = useMemo(
    () => buildNaturalLanguageList(promotionSharePlatforms),
    [promotionSharePlatforms]
  );
  const artistProfileShareUrl = useMemo(() => {
    const websiteSlug = String(submission?.gig?.websiteSlug || '').trim();
    if (!websiteSlug || typeof window === 'undefined') return '';
    return `${window.location.origin}/sites/${websiteSlug}/artists`;
  }, [submission?.gig?.websiteSlug]);

  const updateForm = (patch) => {
    setForm((current) => ({ ...current, ...patch }));
  };

  const handleArtworkSelected = async (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (!selectedFiles.length) return;

    try {
      setUploadingArtwork(true);
      setError('');
      setSuccessMessage('');
      setSubmittedJustNow(false);
      const existingCount = Array.isArray(form.artworkPreviewUrls) ? form.artworkPreviewUrls.length : 0;
      if (existingCount + selectedFiles.length > 10) {
        throw new Error(`You can upload up to 10 images. ${10 - existingCount} slot${10 - existingCount === 1 ? '' : 's'} remaining.`);
      }

      let currentCreativeId = form.artworkCreativeId || submission?.performer?.artworkCreativeId || null;
      let latestPerformer = null;

      for (const file of selectedFiles) {
        const presigned = await presignArtwork(token, file.name);
        const uploadResponse = await fetch(presigned.url, {
          method: 'PUT',
          headers: {
            'Content-Type': file.type || 'application/octet-stream',
          },
          body: file,
        });
        if (!uploadResponse.ok) {
          throw new Error(`Artwork upload failed (${uploadResponse.status})`);
        }

        const finalized = await finalizeArtwork(token, {
          key: presigned.key,
          filename: file.name,
          fileType: file.type || 'image/png',
          title: `${submission?.gig?.title || 'Gig'} artwork`,
          existingCreativeId: currentCreativeId,
        });
        latestPerformer = finalized?.performer || null;
        currentCreativeId = latestPerformer?.artworkCreativeId || currentCreativeId;
      }

      setSubmission((current) => (
        current && latestPerformer
          ? {
              ...current,
              performer: {
                ...current.performer,
                artworkCreativeId: latestPerformer.artworkCreativeId || null,
                artworkPreviewUrl: latestPerformer.artworkPreviewUrl || null,
                artworkPreviewUrls: Array.isArray(latestPerformer.artworkPreviewUrls)
                  ? latestPerformer.artworkPreviewUrls
                  : (latestPerformer.artworkPreviewUrl ? [latestPerformer.artworkPreviewUrl] : []),
              },
            }
          : current
      ));
      updateForm({
        artworkCreativeId: latestPerformer?.artworkCreativeId || null,
        artworkPreviewUrl: latestPerformer?.artworkPreviewUrl || null,
        artworkPreviewUrls: Array.isArray(latestPerformer?.artworkPreviewUrls)
          ? latestPerformer.artworkPreviewUrls
          : (latestPerformer?.artworkPreviewUrl ? [latestPerformer.artworkPreviewUrl] : []),
      });
      setSuccessMessage(
        selectedFiles.length === 1
          ? '1 artwork image uploaded.'
          : `${selectedFiles.length} artwork images uploaded.`
      );
    } catch (err) {
      setError(err.message || 'Failed to upload artwork');
    } finally {
      setUploadingArtwork(false);
      event.target.value = '';
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationError = validateGigSubmissionForm(form);
    if (validationError) {
      setError(validationError);
      setSuccessMessage('');
      setSubmittedJustNow(false);
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      setSuccessMessage('');
      const nextSubmission = await submitForm(token, buildGigSubmissionRequestBody(form));
      setSubmission(nextSubmission);
      setForm(createGigSubmissionForm(nextSubmission));
      setSuccessMessage('Your performer information has been submitted.');
      setSubmittedJustNow(true);
    } catch (err) {
      setError(err.message || 'Failed to submit');
      setSuccessMessage('');
      setSubmittedJustNow(false);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <main className="gig-submission-page gig-submission-empty">
        <div className="gig-submission-panel gig-submission-panel--content">
          <h1>Loading submission link…</h1>
          <p>Please wait while we verify your invitation.</p>
        </div>
      </main>
    );
  }

  if (!submission) {
    return (
      <main className="gig-submission-page gig-submission-empty">
        <div className="gig-submission-panel gig-submission-panel--content">
          <h1>Submission link unavailable</h1>
          <p>{error || 'This invite could not be found.'}</p>
        </div>
      </main>
    );
  }

  return (
    <main className="gig-submission-page">
      <div className="gig-submission-shell">
        <section className="gig-submission-hero">
          <div className="gig-submission-panel gig-submission-panel--content">
            <div className="gig-submission-kicker">Performer Confirmation</div>
            <h1 className="gig-submission-title">Thank you for agreeing to perform at {venueName}.</h1>
            <p className="gig-submission-lead">
              Please complete this form to confirm <strong>{gigTitle}</strong> and give us as much information as possible so we can promote you properly before the gig.
            </p>
            <p className="gig-submission-copy">
              Add your bio, artwork, and every social or music link you want us to push. We keep approved profile details on our website artist page so your channels can keep benefiting from that visibility after this booking as well, helping both your audience and ours find you more easily. If you would prefer us not to keep that profile live, untick the storage option near the end of the form.
            </p>
            <div className="gig-submission-summary">
              <div className="gig-submission-summary-item">
                <span className="gig-submission-summary-label">Venue</span>
                <strong>{venueName}</strong>
              </div>
              <div className="gig-submission-summary-item">
                <span className="gig-submission-summary-label">Performance</span>
                <strong>{gigTitle}</strong>
              </div>
              <div className="gig-submission-summary-item">
                <span className="gig-submission-summary-label">Schedule</span>
                <strong>{scheduleLabel}</strong>
              </div>
              <div className="gig-submission-summary-item">
                <span className="gig-submission-summary-label">Status</span>
                <strong>{submission.performer.status.replace(/_/g, ' ')}</strong>
              </div>
            </div>
            {submission.gig.description ? (
              <div className="gig-submission-meta">
                <div>{submission.gig.description}</div>
              </div>
            ) : null}
          </div>
        </section>

        <section className="gig-submission-main">
          <form className="gig-submission-panel gig-submission-panel--content gig-submission-body" onSubmit={handleSubmit}>
            <div className="gig-submission-callout">
              <strong>Please complete your performer profile</strong>
              <p>
                Share whichever links and contact details you want us to use for promotion, confirmations, and future bookings. Full URLs, `instagram.com/name`, or `@handle` values are all accepted where supported.
              </p>
            </div>

            <div className="gig-submission-section-heading">
              <h2>Artist Details</h2>
              <p>This is the information we will use to introduce you to our audience.</p>
            </div>

            <div className="gig-submission-form-grid">
              <label className="gig-submission-label">
                <span>Artist Name</span>
                <input
                  className="gig-submission-input"
                  type="text"
                  value={form.displayName}
                  onChange={(event) => updateForm({ displayName: event.target.value })}
                  disabled={isCancelled || submitting}
                />
              </label>

              <label className="gig-submission-label">
                <span>Instagram</span>
                <input
                  className="gig-submission-input"
                  type="text"
                  value={form.instagramUrl}
                  onChange={(event) => updateForm({ instagramUrl: event.target.value })}
                  placeholder="@artistname or instagram.com/artistname"
                  disabled={isCancelled || submitting}
                />
              </label>

              <label className="gig-submission-label">
                <span>Facebook</span>
                <input
                  className="gig-submission-input"
                  type="text"
                  value={form.facebookUrl}
                  onChange={(event) => updateForm({ facebookUrl: event.target.value })}
                  placeholder="@artistpage, facebook.com/artistpage, or full URL"
                  disabled={isCancelled || submitting}
                />
              </label>

              <label className="gig-submission-label">
                <span>X</span>
                <input
                  className="gig-submission-input"
                  type="text"
                  value={form.twitterUrl}
                  onChange={(event) => updateForm({ twitterUrl: event.target.value })}
                  placeholder="@artistname or x.com/artistname"
                  disabled={isCancelled || submitting}
                />
              </label>

              <label className="gig-submission-label">
                <span>Pinterest</span>
                <input
                  className="gig-submission-input"
                  type="text"
                  value={form.pinterestUrl}
                  onChange={(event) => updateForm({ pinterestUrl: event.target.value })}
                  placeholder="@artistname or pinterest.com/artistname"
                  disabled={isCancelled || submitting}
                />
              </label>

              <label className="gig-submission-label">
                <span>Apple Music</span>
                <input
                  className="gig-submission-input"
                  type="text"
                  value={form.appleMusicUrl}
                  onChange={(event) => updateForm({ appleMusicUrl: event.target.value })}
                  placeholder="music.apple.com/... or artist page link"
                  disabled={isCancelled || submitting}
                />
              </label>

              <label className="gig-submission-label">
                <span>Mixcloud</span>
                <input
                  className="gig-submission-input"
                  type="text"
                  value={form.mixcloudUrl}
                  onChange={(event) => updateForm({ mixcloudUrl: event.target.value })}
                  placeholder="@artistname or mixcloud.com/artistname"
                  disabled={isCancelled || submitting}
                />
              </label>

              <label className="gig-submission-label">
                <span>TikTok</span>
                <input
                  className="gig-submission-input"
                  type="text"
                  value={form.tiktokUrl}
                  onChange={(event) => updateForm({ tiktokUrl: event.target.value })}
                  placeholder="@artistname or tiktok.com/@artistname"
                  disabled={isCancelled || submitting}
                />
              </label>

              <label className="gig-submission-label">
                <span>SoundCloud</span>
                <input
                  className="gig-submission-input"
                  type="text"
                  value={form.soundcloudUrl}
                  onChange={(event) => updateForm({ soundcloudUrl: event.target.value })}
                  placeholder="@artistname or soundcloud.com/artistname"
                  disabled={isCancelled || submitting}
                />
              </label>

              <label className="gig-submission-label">
                <span>Spotify</span>
                <input
                  className="gig-submission-input"
                  type="text"
                  value={form.spotifyUrl}
                  onChange={(event) => updateForm({ spotifyUrl: event.target.value })}
                  placeholder="open.spotify.com/... or artist page link"
                  disabled={isCancelled || submitting}
                />
              </label>

              <label className="gig-submission-label">
                <span>YouTube</span>
                <input
                  className="gig-submission-input"
                  type="text"
                  value={form.youtubeUrl}
                  onChange={(event) => updateForm({ youtubeUrl: event.target.value })}
                  placeholder="@artistname or youtube.com/@artistname"
                  disabled={isCancelled || submitting}
                />
              </label>

              <label className="gig-submission-label">
                <span>Genres</span>
                <input
                  className="gig-submission-input"
                  type="text"
                  value={form.genresText}
                  onChange={(event) => updateForm({ genresText: event.target.value })}
                  placeholder="house, disco, afro-house"
                  disabled={isCancelled || submitting}
                />
              </label>
            </div>

            <div className="gig-submission-help">
              Add every social or music profile you want us to promote. The more complete this is, the more effectively we can point people to your channels.
            </div>

            <label className="gig-submission-label">
              <span>Short Bio</span>
              <textarea
                className="gig-submission-textarea"
                value={form.bio}
                onChange={(event) => updateForm({ bio: event.target.value })}
                placeholder="Tell us who you are, how you sound, and why people should come and hear you."
                disabled={isCancelled || submitting}
              />
            </label>

            <div className="gig-submission-section-heading">
              <h2>Contact Details</h2>
              <p>Make it easy for us to reach you quickly if timings, assets, or approvals change.</p>
            </div>

            <div className="gig-submission-form-grid">
              <label className="gig-submission-label">
                <span>Contact Email</span>
                <input
                  className="gig-submission-input"
                  type="email"
                  value={form.contactEmail}
                  onChange={(event) => updateForm({ contactEmail: event.target.value })}
                  placeholder="booking@example.com"
                  disabled={isCancelled || submitting}
                />
              </label>

              <label className="gig-submission-label">
                <span>Phone / WhatsApp</span>
                <input
                  className="gig-submission-input"
                  type="text"
                  value={form.contactPhone}
                  onChange={(event) => updateForm({ contactPhone: event.target.value })}
                  placeholder="+44 7700 900123"
                  disabled={isCancelled || submitting}
                />
              </label>

              <label className="gig-submission-label">
                <span>Preferred Contact Method</span>
                <select
                  className="gig-submission-input gig-submission-select"
                  value={form.preferredContactMethod}
                  onChange={(event) => updateForm({ preferredContactMethod: event.target.value })}
                  disabled={isCancelled || submitting}
                >
                  {PREFERRED_CONTACT_METHOD_OPTIONS.map((option) => (
                    <option key={option.value || 'none'} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="gig-submission-label">
                <span>Preferred Contact Details</span>
                <input
                  className="gig-submission-input"
                  type="text"
                  value={form.preferredContactDetails}
                  onChange={(event) => updateForm({ preferredContactDetails: event.target.value })}
                  placeholder="Best email, number, or DM handle to use first"
                  disabled={isCancelled || submitting}
                />
              </label>
            </div>

            <div className="gig-submission-help">
              Add at least one contact route so we can reach you quickly about timings, approvals, or last-minute changes.
            </div>

            <section className="gig-submission-artwork">
              <div className="gig-submission-section-heading gig-submission-section-heading--tight">
                <h2>Artwork</h2>
                <p>Upload up to 10 images we should use when promoting you for this gig.</p>
              </div>
              {Array.isArray(form.artworkPreviewUrls) && form.artworkPreviewUrls.length ? (
                <div className="gig-submission-artwork-gallery">
                  {form.artworkPreviewUrls.map((url, index) => (
                    <div key={`${url}-${index}`} className="gig-submission-artwork-preview">
                      <img src={url} alt={form.displayName || `Artwork preview ${index + 1}`} />
                    </div>
                  ))}
                </div>
              ) : null}
              <input
                className="gig-submission-file"
                type="file"
                accept="image/*"
                multiple
                onChange={handleArtworkSelected}
                disabled={isCancelled || submitting || uploadingArtwork}
              />
              <div>
                {uploadingArtwork
                  ? 'Uploading artwork…'
                  : Array.isArray(form.artworkPreviewUrls) && form.artworkPreviewUrls.length
                    ? `${form.artworkPreviewUrls.length} image${form.artworkPreviewUrls.length === 1 ? '' : 's'} attached.`
                    : 'No artwork uploaded yet.'}
              </div>
            </section>

            {!submittedJustNow && successMessage ? (
              <div className="gig-submission-status gig-submission-status--success">{successMessage}</div>
            ) : null}
            {error ? <div className="gig-submission-status gig-submission-status--error">{error}</div> : null}
            {submittedJustNow ? (
              <div className="gig-submission-status gig-submission-status--success gig-submission-status--stacked">
                <strong>Thanks for sharing your information.</strong>
                <p>We will do our best to promote you to our audience before the gig.</p>
                {artistProfileShareUrl ? (
                  <>
                    <p>
                      Please return the favour by sharing this link with your audience
                      {promotionSharePlatformLabel ? ` on ${promotionSharePlatformLabel}` : ' on your own channels'} as well.
                    </p>
                    <a
                      className="gig-submission-share-link"
                      href={artistProfileShareUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {artistProfileShareUrl}
                    </a>
                  </>
                ) : (
                  <p>
                    Once your artist page is live, please return the favour by sharing it across your own channels as well.
                  </p>
                )}
              </div>
            ) : null}
            {submission.submission.alreadySubmitted && !submittedJustNow ? (
              <div className="gig-submission-status gig-submission-status--success">
                This link has already been used. You can update the details below and resubmit if anything changed.
              </div>
            ) : null}
            {isCancelled || !submission.submission.canSubmit ? (
              <div className="gig-submission-status gig-submission-status--error">
                This gig is no longer accepting submissions.
              </div>
            ) : null}

            <label className="gig-submission-checkbox">
              <input
                type="checkbox"
                checked={!!form.confirmed}
                onChange={(event) => updateForm({ confirmed: event.target.checked })}
                disabled={isCancelled || submitting}
              />
              <span>I confirm I am scheduled to perform at this event.</span>
            </label>

            <label className="gig-submission-checkbox">
              <input
                type="checkbox"
                checked={!!form.storeProfile}
                onChange={(event) => updateForm({ storeProfile: event.target.checked })}
                disabled={isCancelled || submitting}
              />
              <span>
                Keep my artist profile, artwork, bio, and links on your website artist page for future gigs and ongoing visibility. Untick this if you would prefer us not to keep the profile live after this booking.
              </span>
            </label>

            <label className="gig-submission-checkbox">
              <input
                type="checkbox"
                checked={!!form.allowContactSharing}
                onChange={(event) => updateForm({ allowContactSharing: event.target.checked })}
                disabled={isCancelled || submitting}
              />
              <span>
                I would like the venue to share my personal contact details with other event managers and venue owners who enquire about booking me.
              </span>
            </label>

            <div className="gig-submission-actions">
              <button
                className="gig-submission-button gig-submission-button--primary"
                type="submit"
                disabled={isCancelled || submitting || uploadingArtwork}
              >
                {submitting ? 'Submitting…' : submission.submission.alreadySubmitted ? 'Update Submission' : 'Submit Performer Information'}
              </button>
              <button
                className="gig-submission-button gig-submission-button--ghost"
                type="button"
                disabled={submitting || uploadingArtwork}
                onClick={() => setForm(createGigSubmissionForm(submission))}
              >
                Reset Form
              </button>
            </div>
          </form>
        </section>
      </div>
    </main>
  );
}
